import { useEffect, useRef, useState } from 'react';
import { Loader2, Send, MessageSquare, CheckCircle2 } from 'lucide-react';
import type { InterviewMessage } from '../../types/visitReport';

interface InterviewChatProps {
    conversation: InterviewMessage[];
    pendingQuestion: string | null;
    isSending: boolean;
    isComplete: boolean;
    onSendAnswer: (answer: string) => void;
    onFinishEarly: () => void;
}

export function InterviewChat({ conversation, pendingQuestion, isSending, isComplete, onSendAnswer, onFinishEarly }: InterviewChatProps) {
    const [input, setInput] = useState('');
    const scrollerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (scrollerRef.current) {
            scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
        }
    }, [conversation, pendingQuestion]);

    const submit = () => {
        const trimmed = input.trim();
        if (!trimmed || isSending) return;
        onSendAnswer(trimmed);
        setInput('');
    };

    return (
        <div className="flex flex-col gap-3">
            <div ref={scrollerRef} className="flex-1 overflow-y-auto space-y-3 max-h-[50vh] border border-gray-200 rounded-xl p-3 bg-white">
                {conversation.length === 0 && !pendingQuestion && (
                    <div className="text-sm text-gray-400 text-center py-8">
                        <MessageSquare className="w-6 h-6 mx-auto mb-2 opacity-40" />
                        The AI will ask follow-up questions here to complete the report.
                    </div>
                )}
                {conversation.map((m, idx) => (
                    <div key={idx} className={`flex ${m.role === 'assistant' ? 'justify-start' : 'justify-end'}`}>
                        <div className={`max-w-[85%] px-3 py-2 rounded-2xl text-sm break-words ${
                            m.role === 'assistant'
                                ? 'bg-blue-50 text-blue-900 rounded-tl-sm'
                                : 'bg-gray-100 text-gray-800 rounded-tr-sm'
                        }`}>
                            {m.content}
                        </div>
                    </div>
                ))}
                {isSending && (
                    <div className="flex justify-start">
                        <div className="px-3 py-2 rounded-2xl bg-blue-50 text-blue-600 flex items-center gap-2 text-sm">
                            <Loader2 className="w-4 h-4 animate-spin" /> Thinking …
                        </div>
                    </div>
                )}
            </div>

            {isComplete ? (
                <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-green-50 text-green-700 text-sm">
                    <CheckCircle2 className="w-5 h-5" />
                    The report looks complete. You can review it below.
                </div>
            ) : (
                <>
                    <div className="flex gap-2">
                        <textarea
                            value={input}
                            onChange={e => setInput(e.target.value)}
                            onKeyDown={e => {
                                if (e.key === 'Enter' && !e.shiftKey) {
                                    e.preventDefault();
                                    submit();
                                }
                            }}
                            placeholder={pendingQuestion ? 'Your answer …' : 'Add anything you want to clarify …'}
                            rows={2}
                            disabled={isSending}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-xl text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50"
                        />
                        <button
                            type="button"
                            onClick={submit}
                            disabled={!input.trim() || isSending}
                            className="px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 disabled:bg-gray-300 flex items-center gap-1"
                        >
                            <Send className="w-4 h-4" />
                        </button>
                    </div>
                    <button
                        type="button"
                        onClick={onFinishEarly}
                        disabled={isSending}
                        className="text-xs text-gray-500 hover:text-gray-800 underline self-start"
                    >
                        I'm done — skip further questions
                    </button>
                </>
            )}
        </div>
    );
}
